import React, { useState, useEffect, useRef } from "react";
import InvitationSection from "./InvitationSection";
import GallerySection from "./GallerySection";
import LocationSection from "./LocationSection";
import GuestbookSection from "./GuestbookSection";
import HeroSection from "./HeroSection";
import { getPageVisibility } from "../firebase";
import "./Home.css";

interface PageVisibility {
  hero: boolean;
  invitation: boolean;
  gallery: boolean;
  location: boolean;
  guestbook: boolean;
}

const Home: React.FC = () => {
  const [pageVisibility, setPageVisibility] = useState<PageVisibility>({
    hero: true,
    invitation: true,
    gallery: true,
    location: true,
    guestbook: true,
  });
  const [loading, setLoading] = useState(true);
  const [kakaoLoaded, setKakaoLoaded] = useState(false);

  useEffect(() => {
    const loadPageVisibility = async () => {
      try {
        const visibility = await getPageVisibility();
        setPageVisibility(visibility);
      } catch (error) {
        console.error("Error loading page visibility:", error);
        // 에러 발생 시 기본값 사용
      } finally {
        setLoading(false);
      }
    };

    loadPageVisibility();
  }, []);

  // 1. SDK 로드
  useEffect(() => {
    if (window.Kakao) {
      setKakaoLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://t1.kakaocdn.net/kakao_js_sdk/2.7.5/kakao.min.js";
    script.async = true;
    script.onload = () => setKakaoLoaded(true);
    document.body.appendChild(script);
  }, []);

  // 카카오톡 공유 직접 호출
  const handleKakaoShare = (e: React.MouseEvent) => {
    e.preventDefault();
    if (kakaoLoaded && window.Kakao && window.Kakao.Share) {
      if (!window.Kakao.isInitialized()) {
        window.Kakao.init("ce21a9a0584e402f42f6e9bc767aa4b9");
      }
      window.Kakao.Share.sendCustom({
        templateId: 121583,
        templateArgs: { REGI_WEB_DOMAIN: "https://wedding.j1n.uk" },
      });
    } else {
      alert(
        "카카오톡 공유 기능을 불러오는 중입니다. 잠시 후 다시 시도해 주세요."
      );
    }
  };

  if (loading) {
    return <div className="home loading">페이지를 불러오는 중...</div>;
  }

  return (
    <div className="home">
      {/* Hero Section */}
      {pageVisibility.hero && (
        <div className="section-transition">
          <HeroSection />
        </div>
      )}

      {/* Invitation Section */}
      {pageVisibility.invitation && (
        <div className="section-transition">
          <InvitationSection />
        </div>
      )}

      {/* Gallery Section */}
      {pageVisibility.gallery && (
        <div className="section-transition">
          <GallerySection />
        </div>
      )}

      {/* Location Section */}
      {pageVisibility.location && (
        <div className="section-transition">
          <LocationSection />
        </div>
      )}

      {/* Guestbook Section */}
      {pageVisibility.guestbook && (
        <div className="section-transition">
          <GuestbookSection />
        </div>
      )}

      {/* 카카오톡 공유하기/사이트 주소 복사하기 버튼 - 페이지 맨 하단 */}
      <div
        style={{
          margin: "48px 0 96px 0",
          display: "flex",
          justifyContent: "center",
          gap: 16,
        }}
      >
        {/* 카카오톡 공유하기 버튼 */}
        <button
          id="kakaotalk-sharing-btn"
          type="button"
          onClick={handleKakaoShare}
          style={{
            display: "flex",
            alignItems: "center",
            background: "none",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            opacity: 0.85,
            transition: "opacity 0.2s",
          }}
          onMouseOver={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseOut={(e) => (e.currentTarget.style.opacity = "0.85")}
        >
          <img
            src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_medium.png"
            alt="카카오톡 공유 보내기 버튼"
            style={{
              marginRight: 8,
              width: 28,
              height: 28,
              borderRadius: 4,
              background: "none",
              boxShadow: "none",
            }}
          />
          <span
            style={{
              color: "#381e1f",
              fontWeight: 700,
              fontSize: 18,
              letterSpacing: -1,
            }}
          >
            카카오톡 공유하기
          </span>
        </button>
        {/* 사이트 주소 복사하기 버튼 */}
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText("https://wedding.j1n.uk");
            alert("주소가 복사되었습니다!");
          }}
          style={{
            display: "flex",
            alignItems: "center",
            background: "none",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            opacity: 0.85,
            transition: "opacity 0.2s",
            gap: 6,
          }}
          onMouseOver={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseOut={(e) => (e.currentTarget.style.opacity = "0.85")}
        >
          <svg width="22" height="22" fill="#3182f6" viewBox="0 0 20 20">
            <rect
              x="5"
              y="5"
              width="10"
              height="12"
              rx="2"
              fill="#3182f6"
              fillOpacity="0.13"
            />
            <rect
              x="7"
              y="3"
              width="10"
              height="12"
              rx="2"
              stroke="#3182f6"
              strokeWidth="2"
              fill="none"
            />
          </svg>
          <span
            style={{
              color: "#222",
              fontWeight: 700,
              fontSize: 18,
              letterSpacing: -1,
            }}
          >
            사이트 주소 복사하기
          </span>
        </button>
      </div>
      {/* iOS 전용 플로팅 버튼 */}
      {(() => {
        // iOS 감지 (iPad, iPhone, iPod, Mac에서 터치 지원)
        const isIOS =
          typeof window !== "undefined" &&
          /iPad|iPhone|iPod/.test(navigator.userAgent) &&
          !(window as any).MSStream;
        if (!isIOS) return null;
        return (
          <a
            href="https://www.icloud.com/invites/07eQgaWYG0QzEiZldT9ch_vxg"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              position: "fixed",
              right: 12,
              bottom: 32,
              zIndex: 10000,
              width: 54,
              height: 54,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              background: "none",
              border: "none",
              boxShadow: "none",
            }}
          >
            <img
              src="/assets/apple-invitatino.svg"
              alt="iOS 캘린더 초대"
              style={{ width: 54, height: 54, display: "block", opacity: 0.7 }}
            />
          </a>
        );
      })()}
    </div>
  );
};

export default Home;
